import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, DollarSign, Building, Tag, Clock, ArrowLeft, Trash2, Target, FileText, Unlock, ShoppingCart, AlertCircle, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useJob } from '@/hooks/useJobs';
import { getStatusColor, formatCurrency, JOB_CATEGORIES } from '@/lib/types/job';
import { Link } from 'react-router-dom';
import { RFQTab } from '@/components/jobs/RFQTab';
import { WorkflowProgress } from '@/components/workflow/WorkflowProgress';
import { UnifiedJobTimeline } from '@/components/milestones/UnifiedJobTimeline';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MilestoneStatusCard } from '@/components/milestones/MilestoneStatusCard';
import { JobDocumentsTab } from '@/components/files/JobDocumentsTab';
import { MessagesTab } from '@/components/jobs/MessagesTab';
import { CompletionValidationModal } from '@/components/jobs/CompletionValidationModal';
import { ClientReviewModal } from '@/components/jobs/ClientReviewModal';
import { AdminReopenModal } from '@/components/jobs/AdminReopenModal';
import { useJobCompletion } from '@/hooks/useJobCompletion';
import { useAuth } from '@/lib/auth';
import { PostJobReviewFlow } from '@/components/reviews/PostJobReviewFlow';
import { useReviews } from '@/hooks/useReviews';
import { ReviewSubmissionModal } from '@/components/reviews/ReviewSubmissionModal';
import { PurchaseAgreementModal } from '@/components/milestones/PurchaseAgreementModal';
import { PaymentProcessingModal } from '@/components/milestones/PaymentProcessingModal';
import { useCreatePurchaseOrder } from '@/hooks/usePurchaseOrders';
import { useMilestoneApproval, useUpdateMilestone, useMarkMilestoneComplete, useRequestMilestoneChange } from '@/hooks/useMilestones';
import { Milestone, UpdateMilestoneData } from '@/lib/types/milestone';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MilestoneForm } from '@/components/milestones/MilestoneForm';
import { MilestoneCompletionDialog } from '@/components/milestones/MilestoneCompletionDialog';
import { ActivityLog } from '@/components/jobs/ActivityLog';
import { CompletionRejectionModal } from '@/components/jobs/CompletionRejectionModal';
import { useDismissedUIElements } from '@/hooks/useDismissedUIElements';
import { DeadlineTypeBadge, DeadlineType } from '@/components/jobs/DeadlineTypeBadge';
import { ShippingTab } from '@/components/jobs/ShippingTab';
import { PaymentStatusCard } from '@/components/jobs/PaymentStatusCard';
import { usePaymentTransaction } from '@/hooks/usePaymentTransactions';
import { PaymentSetupStatusBanner } from '@/components/jobs/PaymentSetupStatusBanner';
import { usePaymentSetupCoordination } from '@/hooks/usePaymentSetupCoordination';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AdjustmentPaymentsList } from '@/components/purchase-orders/AdjustmentPaymentsList';
import { useQuery } from '@tanstack/react-query';
import { CopiedJobNotice } from '@/components/jobs/CopiedJobNotice';
import { PaymentMethodSelectionModal } from '@/components/payments/PaymentMethodSelectionModal';
import { AchPendingBanner } from '@/components/payments/AchPendingBanner';
import { InvoicePendingCard } from '@/components/payments/InvoicePendingCard';
import { format } from 'date-fns';
import { useJobPendingInvoice } from '@/hooks/useJobPendingInvoice';
import { useCustomerPaymentMethods } from '@/hooks/useCustomerPaymentMethods';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { calculateCcProcessingFee } from '@/lib/paymentUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FileDisputeModal } from '@/components/disputes/FileDisputeModal';
import { DisputeStatusBanner } from '@/components/disputes/DisputeStatusBanner';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import { useTaxPreview } from '@/hooks/useTaxPreview';

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { job, loading, error, refetch: refetchJob } = useJob(jobId!);
  const { user } = useAuth();
  const { data: paymentTransaction } = usePaymentTransaction(job?.id || '');
  const { data: pendingInvoice, refetch: refetchPendingInvoice } = useJobPendingInvoice(job?.id);
  const { data: taxPreview } = useTaxPreview(job?.id);
  
  const {
    requirements,
    completionData,
    completion,
    loading: completionLoading,
    submitting,
    submitCompletion,
    reviewCompletion,
    adminReopenJob,
    getTimeRemaining,
    refetch: refetchCompletion
  } = useJobCompletion(jobId!);
  
  // Payment setup coordination
  const { paymentStatus, userRole: paymentUserRole, refetch: refetchPaymentStatus, loading: paymentStatusLoading } = usePaymentSetupCoordination(
    job?.id || '',
    job?.public_id || jobId || '',
    job?.status
  );

  // Get payment methods for CC fee calculation (only for clients)
  const isClientUser = user?.role === 'client';
  const { paymentMethods } = useCustomerPaymentMethods(isClientUser ? (user?.organization_id || '') : '');
  const { data: ccFeeSetting } = usePlatformSetting('cc_processing_fee_percentage');
  const ccFeePercentage = ccFeeSetting ? parseFloat(ccFeeSetting.setting_value) : 2.9;

  // States for modals
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAdminReopenModal, setShowAdminReopenModal] = useState(false);
  const [showPostJobReview, setShowPostJobReview] = useState(false);
  const [showSubmitReviewModal, setShowSubmitReviewModal] = useState(false);
  const [showPurchaseAgreementModal, setShowPurchaseAgreementModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showPaymentProcessingModal, setShowPaymentProcessingModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentProcessingStatus, setPaymentProcessingStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentError, setPaymentError] = useState<string>('');
  const [paymentErrorDetails, setPaymentErrorDetails] = useState<{
    recommendation?: string;
    code?: string;
    canRetry?: boolean;
    suggestAch?: boolean;
    suggestUpdatePayment?: boolean;
    severity?: 'temporary' | 'permanent' | 'requires_action';
  }>();
  const [paymentTransactionId, setPaymentTransactionId] = useState<string>('');
  
  // Job-related data
  const [vendorUserId, setVendorUserId] = useState<string | undefined>();
  const [clientUserId, setClientUserId] = useState<string | undefined>();
  const [hasPurchaseOrder, setHasPurchaseOrder] = useState(false);
  const [purchaseOrderId, setPurchaseOrderId] = useState<string | undefined>();
  const [purchaseOrderAccepted, setPurchaseOrderAccepted] = useState(false);
  const [winningBidId, setWinningBidId] = useState<string | undefined>();
  const [currentPaymentMethodId, setCurrentPaymentMethodId] = useState<string | undefined>();
  const [isCopiedJob, setIsCopiedJob] = useState(false);
  const [achPending, setAchPending] = useState(false);
  const [achSettlementDate, setAchSettlementDate] = useState<string>('');
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'overview';
  });
  
  // Milestone states and handlers
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(null);
  const [showEditMilestoneDialog, setShowEditMilestoneDialog] = useState(false);
  const [completingMilestone, setCompletingMilestone] = useState<Milestone | null>(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [requestingChangeMilestone, setRequestingChangeMilestone] = useState<Milestone | null>(null);
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  
  // Milestone hooks
  const { updateMilestone } = useUpdateMilestone();
  const { markComplete } = useMarkMilestoneComplete();
  const { requestChange } = useRequestMilestoneChange();
  
  // Fetch adjustment payment transactions
  const { data: adjustmentTransactions } = useQuery({
    queryKey: ['adjustment-transactions', job?.id, purchaseOrderId],
    queryFn: async () => {
      if (!job?.id || !purchaseOrderId) return [];
      
      const { data: po } = await supabase
        .from('purchase_orders')
        .select('id')
        .eq('public_id', purchaseOrderId)
        .maybeSingle();
      
      if (!po?.id) return [];
      
      const { data } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('purchase_order_id', po.id)
        .eq('payment_context', 'adjustment_payment')
        .order('created_at', { ascending: false });
      
      return data || [];
    },
    enabled: !!job?.id && !!purchaseOrderId,
  });

  // Fetch pending adjustments for banner
  const { data: pendingAdjustments } = useQuery({
    queryKey: ['pending-adjustments', purchaseOrderId],
    queryFn: async () => {
      if (!purchaseOrderId) return [];
      
      const { data: po } = await supabase
        .from('purchase_orders')
        .select('id, organization_id, vendor_organization_id')
        .eq('public_id', purchaseOrderId)
        .maybeSingle();
      
      if (!po?.id) return [];
      
      const { data } = await supabase
        .from('po_modification_requests')
        .select('*')
        .eq('purchase_order_id', po.id)
        .eq('status', 'pending');
      
      if (!data) return [];
      
      // Filter to only show adjustments that require THIS user's approval
      return data.filter((adjustment: any) => {
        if (adjustment.initiating_party === 'client') {
          return user?.organization_id === po.vendor_organization_id;
        } else {
          return user?.organization_id === po.organization_id;
        }
      });
    },
    enabled: !!purchaseOrderId && !!user?.organization_id,
  });

  // Fetch PO financial data for payment summary
  const { data: poFinancialData } = useQuery({
    queryKey: ['po-financial-data', purchaseOrderId],
    queryFn: async () => {
      if (!purchaseOrderId) return null;
      
      const { data: po } = await supabase
        .from('purchase_orders')
        .select('id, po_amount_cents, currency')
        .eq('public_id', purchaseOrderId)
        .maybeSingle();
      
      if (!po) return null;
      
      // Get all accepted adjustments to calculate original vs current
      const { data: adjustments } = await supabase
        .from('po_modification_requests')
        .select('adjustment_amount_cents')
        .eq('purchase_order_id', po.id)
        .eq('status', 'accepted');
      
      const totalAdjustments = (adjustments || []).reduce(
        (sum, adj) => sum + (adj.adjustment_amount_cents || 0), 
        0
      );
      
      const originalAmount = po.po_amount_cents - totalAdjustments;
      
      return {
        currentAmount: po.po_amount_cents,
        originalAmount,
        totalAdjustments,
        currency: po.currency,
      };
    },
    enabled: !!purchaseOrderId,
  });

  // Calculate vendor-specific financial totals
  const { data: vendorFinancialTotals } = useQuery({
    queryKey: ['vendor-financial-totals', paymentTransaction?.id, adjustmentTransactions],
    queryFn: async () => {
      if (!paymentTransaction && (!adjustmentTransactions || adjustmentTransactions.length === 0)) {
        return null;
      }
      
      let totalPlatformFees = 0;
      let totalVendorPayout = 0;
      
      // Add initial PO payment fees
      if (paymentTransaction) {
        totalPlatformFees += paymentTransaction.platform_fee_cents;
        totalVendorPayout += paymentTransaction.vendor_payout_cents;
      }
      
      // Add adjustment payment fees
      if (adjustmentTransactions) {
        adjustmentTransactions.forEach(tx => {
          totalPlatformFees += tx.platform_fee_cents;
          totalVendorPayout += tx.vendor_payout_cents;
        });
      }
      
      return {
        totalPlatformFees,
        totalVendorPayout,
      };
    },
    enabled: !!paymentTransaction || (!!adjustmentTransactions && adjustmentTransactions.length > 0),
  });
  
  
  // Milestone approval
  const { approval, refetch: refetchApproval } = useMilestoneApproval(job?.id);
  
  // PO creation
  const { createPurchaseOrder, loading: creatingPO } = useCreatePurchaseOrder();
  
  // Reviews hook
  const { canUserReviewJob, checkPendingReviews } = useReviews();
  const [canReview, setCanReview] = useState(false);
  const [hasPendingReview, setHasPendingReview] = useState(false);

  // Dismissal tracking for completion rejection modal
  const { isDismissed: rejectionDismissed, dismiss: dismissRejection, isLoading: rejectionDismissedLoading } = useDismissedUIElements(
    'completion_rejection_notice',
    { completion_id: completion?.id }
  );

  // Handle return from payment setup cancellation
  useEffect(() => {
    const setupStatus = searchParams.get('setup');
    if (setupStatus === 'cancel') {
      toast({
        title: 'Payment Setup Cancelled',
        description: 'You can complete payment setup at any time from the payment setup page.',
        variant: 'destructive',
      });
      // Clean URL without reloading
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams, toast]);

  // Update active tab when URL changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Handle hash scrolling after tab change
  useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const element = document.querySelector(window.location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [activeTab]);

  // Fetch vendor and client user IDs, PO status when job is available
  useEffect(() => {
    const fetchJobParticipants = async () => {
      if (!job?.id) return;

      try {
        console.log('Fetching job participants for job:', job.id, 'org:', job.organization_id);
        
        // Get the award information to find both parties and winning bid
        const { data: award } = await supabase
          .from('awards')
          .select(`
            awarded_by_user_id,
            winning_bid_id,
            bids (
              vendor_user_id
            )
          `)
          .eq('job_id', job.id)
          .maybeSingle();

        console.log('Award data:', award);
        if (award?.bids?.vendor_user_id) {
          setVendorUserId(award.bids.vendor_user_id);
          console.log('Set vendorUserId:', award.bids.vendor_user_id);
        }
        if (award?.awarded_by_user_id) {
          setClientUserId(award.awarded_by_user_id);
          console.log('Set clientUserId from award:', award.awarded_by_user_id);
        } else {
          console.warn('No awarded_by_user_id found on award for job', job.id);
        }
        if (award?.winning_bid_id) {
          setWinningBidId(award.winning_bid_id);
        }

      // Check for purchase order
        const { data: po } = await supabase
          .from('purchase_orders')
          .select('public_id, status, vendor_acknowledged_at')
          .eq('job_id', job.id)
          .maybeSingle();
        
        if (po?.public_id) {
          setHasPurchaseOrder(true);
          setPurchaseOrderId(po.public_id);
          // Check if vendor has accepted the PO
          setPurchaseOrderAccepted(po.status === 'acknowledged' || !!po.vendor_acknowledged_at);
        }

        // Get payment setup info
        const { data: paymentSetup } = await supabase
          .from('payment_setup_status')
          .select('stripe_payment_method_id')
          .eq('job_id', job.id)
          .maybeSingle();
        
        if (paymentSetup?.stripe_payment_method_id) {
          setCurrentPaymentMethodId(paymentSetup.stripe_payment_method_id);
        }

        // Check for existing ACH pending transactions
        const { data: achTransaction } = await supabase
          .from('payment_transactions')
          .select('status, ach_settlement_expected_at')
          .eq('job_id', job.id)
          .eq('payment_method_type', 'traditional_ach')
          .eq('status', 'ach_pending')
          .is('ach_settled_at', null)
          .maybeSingle();

        if (achTransaction?.ach_settlement_expected_at) {
          setAchPending(true);
          setAchSettlementDate(achTransaction.ach_settlement_expected_at);
        }
      } catch (error) {
        console.error('Error fetching job participants:', error);
      }

      // Check if this is a copied job
      if (job?.copied_from_job_id) {
        setIsCopiedJob(true);
      }
    };

    fetchJobParticipants();
  }, [job?.id, job?.organization_id]);

  // Track job viewed for vendors
  useEffect(() => {
    if (job?.id && user?.role === 'vendor') {
      analytics.track({
        name: ANALYTICS_EVENTS.JOB_VIEWED_VENDOR,
        category: 'engagement',
        properties: {
          job_id: job.id,
          job_status: job.status,
        },
      });
    }
  }, [job?.id, job?.status, user?.role]);

  // Real-time subscription for ACH settlement
  useEffect(() => {
    if (!job?.id || !achPending) return;

    const channel = supabase
      .channel(`ach-settlement-${job.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_transactions',
          filter: `job_id=eq.${job.id}`,
        },
        (payload) => {
          const newData = payload.new as any;
          // If ACH has settled, clear the pending state
          if (newData.ach_settled_at && achPending) {
            setAchPending(false);
            setAchSettlementDate('');
            toast({
              title: 'Payment Settled!',
              description: 'Your ACH payment has cleared and your Purchase Order has been created.',
            });
            // Refresh data
            refetchJob();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [job?.id, achPending]);

  // Refresh completion requirements when the modal opens
  useEffect(() => {
    if (showValidationModal) {
      refetchCompletion();
    }
  }, [showValidationModal, refetchCompletion]);


  const handleDeleteJob = async () => {
    if (!job) return;

    try {
      const { error } = await supabase.rpc('soft_delete_job', { p_public_id: job.public_id });

      if (error) throw error;

      toast({
        title: 'Job deleted',
        description: `Job ${job.public_id} has been successfully deleted.`,
      });

      navigate('/jobs');
    } catch (err) {
      console.error('Error deleting job:', err);
      toast({
        title: 'Error deleting job',
        description: err instanceof Error ? err.message : 'Failed to delete job',
        variant: 'destructive',
      });
    }
  };

  // Explicit role checks - no fallback assumptions
  const isVendor = user?.role === 'vendor';
  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';
  const revieweeId = isVendor ? clientUserId : vendorUserId;
  const showVendorFinancials = isVendor;

  // Check if RFQ needs to be opened
  const needsRFQSetup = job?.status === 'draft' && !job?.rfq;

  // Show rejection modal for vendors when completion was rejected
  useEffect(() => {
    if (
      isVendor &&
      completion?.review_result === 'rejected' &&
      completion?.id &&
      !rejectionDismissed &&
      !rejectionDismissedLoading &&
      !completionLoading
    ) {
      setShowRejectionModal(true);
    }
  }, [isVendor, completion, rejectionDismissed, rejectionDismissedLoading, completionLoading]);

  const handleAcknowledgeRejection = () => {
    dismissRejection();
    setShowRejectionModal(false);
  };

  // Check if user can review and has pending review
  useEffect(() => {
    const checkReviewStatus = async () => {
      const reviewableStatuses = ['accepted', 'closed'];
      if (!job?.id || !user?.id || !reviewableStatuses.includes(job.status)) {
        setCanReview(false);
        setHasPendingReview(false);
        return;
      }

      try {
        const reviewerType = isVendor ? 'vendor' : 'client';
        const canReviewResult = await canUserReviewJob(job.id, reviewerType);
        const pendingReviews = await checkPendingReviews();
        const hasPendingForThisJob = pendingReviews.some(review => review.job_id === job.id);
        
        setCanReview(canReviewResult);
        setHasPendingReview(hasPendingForThisJob);
      } catch (error) {
        console.error('Error checking review status:', error);
        setCanReview(false);
        setHasPendingReview(false);
      }
    };

    checkReviewStatus();
  }, [job?.id, job?.status, user?.id, isVendor, canUserReviewJob, checkPendingReviews]);

  const canMarkComplete = () => {
    if (!job || !isVendor) return false;
    return ['awarded', 'in_progress', 'rejected'].includes(job.status);
  };

  const canReviewCompletion = () => {
    if (!job || !isClient) return false;
    return job.status === 'completed_pending_review';
  };


  const canAdminReopen = () => {
    if (!job || !isAdmin) return false;
    return completionData?.is_archived === true;
  };

  const canShowSubmitReviewButton = () => {
    // Show button if job is accepted and user can review (regardless of pending review status)
    // This handles cases where review records weren't properly created
    return job?.status === 'accepted' && canReview;
  };

  const canFileDispute = () => {
    if (!job) return false;
    // Don't show if already disputed or closed
    if (job.status === 'disputed' || job.status === 'closed') return false;
    // Only show for active job statuses where disputes make sense
    const disputeEligibleStatuses = ['in_progress', 'completed_pending_review', 'accepted', 'rejected'];
    // Must be client or vendor on this job
    return disputeEligibleStatuses.includes(job.status) && (isClient || isVendor);
  };

  const getCompletionButtonText = () => {
    if (!job) return "Mark Complete";
    
    if (job.status === 'completed_pending_review') {
      if (isClient) return "Review Completion";
      if (isVendor) return "Awaiting Client Review";
    }
    
    if (job.status === 'rejected' && isVendor) {
      return "Resubmit Completion";
    }
    
    return "Mark Complete";
  };

  const getTimeRemainingForReview = () => {
    if (!completionData?.completion_submitted_at) return null;
    return getTimeRemaining(completionData.completion_submitted_at);
  };

  const handleCompletionAction = () => {
    if (canMarkComplete()) {
      setShowValidationModal(true);
    } else if (canReviewCompletion()) {
      setShowReviewModal(true);
    }
  };

  
  const handleJobAccepted = async () => {
    console.log('Job accepted - refetching job data');
    // Refresh job data first to get updated status
    await refetchJob();
    // Then trigger review flow
    console.log('Job data refreshed - triggering review flow');
    setShowPostJobReview(true);
  };

  const handleCompletionSubmit = async (completionNotes?: string, deliveryDate?: Date) => {
    const success = await submitCompletion(completionNotes, deliveryDate);
    if (success) {
      // Automatically refresh job details to update UI state (hide button, etc.)
      await refetchJob();
      setShowValidationModal(false);
    }
    return success;
  };

  const handleAcceptAgreement = () => {
    setShowPurchaseAgreementModal(false);
    setShowPaymentMethodModal(true);
  };
  
  // Milestone handlers
  const handleEditMilestone = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setShowEditMilestoneDialog(true);
  };

  const handleUpdateMilestone = async (data: UpdateMilestoneData) => {
    if (!editingMilestone) return;
    
    try {
      await updateMilestone(editingMilestone.id, data);
      setShowEditMilestoneDialog(false);
      setEditingMilestone(null);
      refetchJob();
      toast({
        title: 'Success',
        description: 'Milestone updated successfully',
      });
    } catch (error) {
      console.error('Failed to update milestone:', error);
      toast({
        title: 'Error',
        description: 'Failed to update milestone',
        variant: 'destructive',
      });
    }
  };

  const handleMarkComplete = async (milestoneId: string) => {
    try {
      await markComplete(milestoneId);
      refetchJob();
      toast({
        title: 'Success',
        description: 'Milestone marked as complete',
      });
    } catch (error) {
      console.error('Failed to mark milestone complete:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark milestone complete',
        variant: 'destructive',
      });
    }
  };

  const handleRequestChange = (milestone: Milestone) => {
    setRequestingChangeMilestone(milestone);
    setShowChangeRequestDialog(true);
  };

  const handleSubmitChangeRequest = async (data: UpdateMilestoneData & { change_reason?: string }) => {
    if (!requestingChangeMilestone) return;

    try {
      // Compare form data with original milestone to find changed fields only
      const changedFields: Record<string, any> = {};
      
      const fieldsToCheck = [
        'milestone_name',
        'due_date',
        'description',
        'milestone_type',
        'deliverable',
        'action_owner',
        'milestone_order'
      ];
      
      for (const field of fieldsToCheck) {
        const oldValue = requestingChangeMilestone[field as keyof typeof requestingChangeMilestone];
        const newValue = data[field as keyof typeof data];
        
        // Normalize for comparison (handle null/undefined/empty string)
        const normalizedOld = oldValue ?? '';
        const normalizedNew = newValue ?? '';
        
        if (String(normalizedOld) !== String(normalizedNew)) {
          changedFields[field] = newValue;
        }
      }
      
      // If no fields changed, don't allow the change request
      if (Object.keys(changedFields).length === 0) {
        toast({
          title: 'No Changes',
          description: 'No fields were modified. Please make changes before requesting approval.',
          variant: 'destructive',
        });
        return;
      }

      await requestChange(
        requestingChangeMilestone.id,
        { ...changedFields, change_reason: data.change_reason },
        user?.role === 'client' ? 'client' : 'vendor',
        () => {
          refetchJob();
          refetchApproval();
        }
      );
      setShowChangeRequestDialog(false);
      setRequestingChangeMilestone(null);
      toast({
        title: 'Change Request Submitted',
        description: 'The other party will be notified to review your requested changes.',
      });
    } catch (error) {
      console.error('Failed to request milestone change:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit change request',
        variant: 'destructive',
      });
    }
  };

  const handlePaymentMethodConfirmed = async (selectedPaymentMethodId: string) => {
    if (!job?.id || !winningBidId) {
      toast({
        title: 'Error',
        description: 'Missing job or bid information',
        variant: 'destructive'
      });
      return;
    }

    // Determine if selected method is a card for fee calculation
    const selectedMethod = paymentMethods?.find(
      pm => pm.stripe_payment_method_id === selectedPaymentMethodId
    );
    const isCardPayment = selectedMethod?.payment_method_type === 'card';

    try {
      console.log('[PO Flow] Starting purchase order creation with payment method:', selectedPaymentMethodId);
      
      setShowPaymentMethodModal(false);
      
      await createPurchaseOrder(
        job.id, 
        winningBidId, 
        true,
        {
          selectedPaymentMethodId,
          onPaymentStart: (baseAmount: number) => {
            // Calculate total with CC fee if card payment using pass-through formula
            const ccProcessingFeeCents = isCardPayment 
              ? calculateCcProcessingFee(baseAmount, ccFeePercentage) 
              : 0;
            const totalAmount = baseAmount + ccProcessingFeeCents;
            
            setPaymentAmount(totalAmount);
            setPaymentProcessingStatus('processing');
            setPaymentError('');
            setPaymentTransactionId('');
            setShowPaymentProcessingModal(true);
          },
          onPaymentSuccess: (transactionId: string) => {
            setPaymentProcessingStatus('success');
            setPaymentTransactionId(transactionId);
            
            // Auto-close after 2 seconds and navigate
            setTimeout(async () => {
              setShowPaymentProcessingModal(false);
              
              const { data: po } = await supabase
                .from('purchase_orders')
                .select('public_id')
                .eq('job_id', job.id)
                .maybeSingle();
              
              if (po?.public_id) {
                setHasPurchaseOrder(true);
                setPurchaseOrderId(po.public_id);
                navigate(`/purchase-orders/${po.public_id}`);
              }
            }, 2000);
          },
          onPaymentError: (error: string, details?: {
            recommendation?: string;
            code?: string;
            canRetry?: boolean;
            suggestAch?: boolean;
            suggestUpdatePayment?: boolean;
            severity?: 'temporary' | 'permanent' | 'requires_action';
          }) => {
            setPaymentProcessingStatus('error');
            setPaymentError(error);
            setPaymentErrorDetails(details);
          },
          onAchPending: (settlementEta: string) => {
            // Close processing modal
            setShowPaymentProcessingModal(false);
            
            // Store ACH pending state
            setAchPending(true);
            setAchSettlementDate(settlementEta);
            
            let settlementDateText = '3-5 business days';
            if (settlementEta) {
              try {
                settlementDateText = format(new Date(settlementEta), 'MMM d, yyyy');
              } catch (error) {
                console.error('Invalid settlement date:', settlementEta, error);
              }
            }
            
            toast({
              title: 'ACH Payment Initiated',
              description: `Your bank transfer is being processed. The Purchase Order will be automatically created when payment settles in ${settlementDateText}.`,
              duration: 7000,
            });
          },
          onInvoicePending: (invoice) => {
            // Close processing modal
            setShowPaymentProcessingModal(false);
            
            toast({
              title: 'Invoice Sent',
              description: `Invoice #${invoice.invoiceNumber} has been sent. PO will be created when paid.`,
              duration: 7000,
            });
            
            // Refetch job data and pending invoice to show the invoice banner immediately
            refetchJob();
            refetchPendingInvoice();
          }
        }
      );
    } catch (error) {
      console.error('Failed to create PO:', error);
      if (!showPaymentProcessingModal) {
        toast({
          title: 'Error',
          description: 'Failed to process payment. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleReviewDetails = () => {
    setShowPurchaseAgreementModal(false);
  };

  const canShowIssuePOButton = () => {
    // Don't show if: not a job, not client, ACH pending, or invoice pending
    if (!job || !isClient || achPending || pendingInvoice) return false;
    // Show if milestones are approved by both parties and no PO exists
    return job.status === 'awarded' && 
           approval?.approval_status === 'approved_by_both' && 
           !hasPurchaseOrder;
  };

  const needsMilestoneApproval = 
    job?.status === 'awarded' && 
    approval && 
    approval.approval_status !== 'approved_by_both' &&
    ((isClient && (approval.approval_status === 'pending_client' || approval.approval_status === 'approved_by_vendor' || approval.approval_status === 'pending_both')) ||
     (isVendor && (approval.approval_status === 'pending_vendor' || approval.approval_status === 'approved_by_client' || approval.approval_status === 'pending_both')));

  const getMilestoneApprovalBanner = () => {
    if (!job || job.status !== 'awarded') return null;
    if (!approval) return null;
    // Don't show milestone approval until payment setup is complete by both parties
    if (!paymentStatus?.both_complete) return null;
    
    const status = approval.approval_status;
    
    if (status === 'approved_by_both') return null; // Already approved
    
    // Only show "other party approved" message when they actually have
    const showOtherPartyApproved = 
      (isClient && status === 'approved_by_vendor') ||
      (isVendor && status === 'approved_by_client');
    
    // Show "needs approval" message for pending statuses
    const needsUserApproval = 
      (isClient && (status === 'pending_both' || status === 'pending_client')) ||
      (isVendor && (status === 'pending_both' || status === 'pending_vendor'));
    
    if (showOtherPartyApproved) {
      return (
        <Alert className="border-orange-500 bg-orange-50 mb-6">
          <AlertCircle className="h-5 w-5 text-orange-600 animate-pulse" />
          <div className="flex items-start justify-between gap-4 flex-1">
            <div className="flex-1">
              <AlertTitle className="text-orange-900 font-semibold">Action Required: Approve Milestones</AlertTitle>
              <AlertDescription className="text-orange-800">
                {isClient ? 'The vendor has approved the milestone timeline.' : 'The client has approved the milestone timeline.'}
                {' '}Review and approve to proceed with project execution.
              </AlertDescription>
            </div>
            <Button 
              onClick={() => navigate(`/jobs/${job.public_id}/milestones`)}
              className="bg-orange-600 hover:bg-orange-700 flex-shrink-0"
              size="sm"
            >
              Go to Milestones
            </Button>
          </div>
        </Alert>
      );
    } else if (needsUserApproval) {
      return (
        <Alert className="border-orange-500 bg-orange-50 mb-6">
          <AlertCircle className="h-5 w-5 text-orange-600 animate-pulse" />
          <div className="flex items-start justify-between gap-4 flex-1">
            <div className="flex-1">
              <AlertTitle className="text-orange-900 font-semibold">Action Required: Approve Milestones</AlertTitle>
              <AlertDescription className="text-orange-800">
                Please review and approve the milestone timeline to proceed with project execution.
              </AlertDescription>
            </div>
            <Button 
              onClick={() => navigate(`/jobs/${job.public_id}/milestones`)}
              className="bg-orange-600 hover:bg-orange-700 flex-shrink-0"
              size="sm"
            >
              Go to Milestones
            </Button>
          </div>
        </Alert>
      );
    } else {
      // Waiting for other party
      return (
        <Alert className="border-blue-500 bg-blue-50 mb-6">
          <Clock className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900 font-semibold">Waiting for {isClient ? 'Vendor' : 'Client'} Approval</AlertTitle>
          <AlertDescription className="text-blue-800">
            You've approved the milestone timeline. Waiting for {isClient ? 'the vendor' : 'your client'} to review and approve.
          </AlertDescription>
        </Alert>
      );
    }
  };

  const handlePOButtonClick = async () => {
    if (hasPurchaseOrder && purchaseOrderId) {
      navigate(`/purchase-orders/${purchaseOrderId}`);
    } else {
      // Calculate payment amount before showing modal
      if (winningBidId) {
        try {
          const { data: award } = await supabase
            .from('awards')
            .select('id')
            .eq('winning_bid_id', winningBidId)
            .single();
          
          if (award) {
            const { data: selections } = await supabase
              .from('award_selections')
              .select('total_price_cents')
              .eq('award_id', award.id);
            
            const total = selections?.reduce((sum, s) => sum + s.total_price_cents, 0) || 0;
            setPaymentAmount(total);
          }
        } catch (error) {
          console.error('Error calculating payment amount:', error);
        }
      }
      setShowPurchaseAgreementModal(true);
    }
  };
  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-8">Loading job details...</div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600 py-8">
          {error || 'Job not found'}
        </div>
      </div>
    );
  }

  const categoryLabel = JOB_CATEGORIES.find(c => c.value === job.category)?.label || job.category;

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
      {/* Pending Change Orders Banner */}
      {pendingAdjustments && pendingAdjustments.length > 0 && (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Pending Change Orders</AlertTitle>
          <AlertDescription className="text-sm">
            {pendingAdjustments.length} change order request(s) require your review.
            <Button 
              variant="link" 
              className="px-2 text-sm" 
              onClick={() => navigate(`/purchase-orders/${purchaseOrderId}?tab=adjustments`)}
            >
              Review Now →
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Payment Setup Status Banner */}
      {job.status === 'awarded' && paymentStatus && (
        <div className="mb-6">
          <PaymentSetupStatusBanner
            jobId={job.public_id}
            clientComplete={paymentStatus.client_setup_complete || false}
            vendorComplete={paymentStatus.vendor_setup_complete || false}
            bothComplete={paymentStatus.both_complete || false}
            userRole={paymentUserRole}
            hasAnyMilestoneApproval={
              approval?.approval_status !== 'pending_both' && 
              approval?.approval_status !== undefined
            }
            onRefresh={refetchPaymentStatus}
            isRefreshing={paymentStatusLoading}
          />
        </div>
      )}
      
      {/* Milestone Approval Banner */}
      {getMilestoneApprovalBanner()}
      
      {/* ACH Pending Banner */}
      {achPending && achSettlementDate && (
        <div className="mb-6">
          <AchPendingBanner 
            settlementDate={achSettlementDate}
            showDetailedMessage={true}
          />
        </div>
      )}

      {/* Invoice Pending Card - Show when job has a pending invoice */}
      {pendingInvoice && isClient && (
        <div className="mb-6">
          <InvoicePendingCard
            invoiceNumber={pendingInvoice.stripe_invoice_number || undefined}
            invoiceUrl={pendingInvoice.hosted_invoice_url || undefined}
            pdfUrl={pendingInvoice.pdf_url || undefined}
            amountCents={pendingInvoice.amount_due_cents}
            status={pendingInvoice.status as 'open' | 'paid' | 'void' | 'uncollectible' | 'payment_failed' | 'draft'}
            dueDate={pendingInvoice.due_date || undefined}
            createdAt={pendingInvoice.created_at}
            paymentContext={pendingInvoice.payment_context as 'initial_payment' | 'change_order' | undefined}
          />
        </div>
      )}
      {isCopiedJob && job.status === 'draft' && (
        <div className="mb-6">
          <CopiedJobNotice jobId={job.id} />
        </div>
      )}
      
      {/* Header */}
      <div className="mb-6">
        <Link to="/jobs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Jobs
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
              <h1 className="text-2xl sm:text-3xl font-bold">{job.title}</h1>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={getStatusColor(job.status)}>
                  {job.status.replace(/_/g, ' ')}
                </Badge>
                {completionData?.is_archived && (
                  <Badge variant="destructive">ARCHIVED</Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap w-full sm:w-auto">
            {job.status === 'draft' && (
              <>
                <Button variant="outline" onClick={() => navigate(`/jobs/${job.public_id}/edit`)} className="flex-1 sm:flex-none">Edit Job</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="flex-1 sm:flex-none">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Job
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete the job "{job.title}" 
                        and remove all associated data including specifications, delivery details, and any RFQs.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteJob} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Delete Job
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}

            {(canShowIssuePOButton() || hasPurchaseOrder) && (
              <Button 
                onClick={handlePOButtonClick}
                disabled={creatingPO}
                variant={hasPurchaseOrder && purchaseOrderAccepted ? "outline" : "default"}
                className={hasPurchaseOrder && !purchaseOrderAccepted ? "bg-green-600 hover:bg-green-700 text-white" : !hasPurchaseOrder ? "bg-green-600 hover:bg-green-700 text-white" : ""}
              >
                {hasPurchaseOrder ? (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    {purchaseOrderAccepted ? 'View Purchase Order' : 'Review Purchase Order'}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    {creatingPO ? 'Processing...' : 'Issue Purchase Order'}
                  </>
                )}
              </Button>
            )}

            {(canMarkComplete() || canReviewCompletion()) && (
              <Button 
                onClick={handleCompletionAction}
                disabled={submitting || (job.status === 'completed_pending_review' && isVendor)}
                variant={canReviewCompletion() ? "default" : (requirements?.milestones_complete ? "default" : "secondary")}
                className={canMarkComplete() && requirements?.milestones_complete ? "bg-green-600 hover:bg-green-700 text-white" : ""}
              >
                <Target className="mr-2 h-4 w-4" />
                {getCompletionButtonText()}
              </Button>
            )}


            {canShowSubmitReviewButton() && (
              <Button 
                onClick={() => setShowSubmitReviewModal(true)}
                variant="outline"
              >
                <FileText className="mr-2 h-4 w-4" />
                Submit Review
              </Button>
            )}

            {canFileDispute() && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowDisputeModal(true)}
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      <Flag className="h-4 w-4" />
                      <span className="sr-only">File Dispute</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>File a Dispute</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {canAdminReopen() && (
              <Button 
                onClick={() => setShowAdminReopenModal(true)}
                variant="destructive"
              >
                <Unlock className="mr-2 h-4 w-4" />
                Reopen Job
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Compact Job Metadata */}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground mb-6 py-2.5 px-4 bg-muted/30 rounded-lg border">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Job ID:</span>
          <span className="font-medium text-foreground">{job.public_id}</span>
        </div>
        
        <span className="text-muted-foreground/40">•</span>
        
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-blue-600" />
          <span className="text-muted-foreground">Due:</span>
          <span className="font-medium text-foreground">
            {new Date(job.due_date).toLocaleDateString()}
          </span>
          {job.delivery_details?.[0]?.deadline_type && (
            <DeadlineTypeBadge 
              type={job.delivery_details[0].deadline_type as DeadlineType}
              showIcon={false}
            />
          )}
        </div>
        
        <span className="text-muted-foreground/40">•</span>
        
        <div className="flex items-center gap-1.5">
          <Tag className="h-4 w-4 text-purple-600" />
          <span className="font-medium text-foreground">{categoryLabel}</span>
        </div>
        
        <span className="text-muted-foreground/40">•</span>
        
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-orange-600" />
          <span>Created {new Date(job.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className={`grid w-full ${job.status === 'awarded' || job.status === 'in_progress' || job.status === 'completed_pending_review' || job.status === 'accepted' || job.status === 'closed' ? 'grid-cols-7 min-w-[700px]' : 'grid-cols-4 min-w-[400px]'} sm:min-w-0`}>
            <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
            <TabsTrigger value="rfq" className={`text-xs sm:text-sm ${needsRFQSetup ? "relative" : ""}`}>
              RFQ
              {needsRFQSetup && (
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-orange-500 animate-pulse" />
              )}
            </TabsTrigger>
            {(job.status === 'awarded' || job.status === 'in_progress' || job.status === 'completed_pending_review' || job.status === 'accepted' || job.status === 'closed') && (
              <>
                <TabsTrigger value="messages" className="text-xs sm:text-sm">Messages</TabsTrigger>
                <TabsTrigger value="payment" className="text-xs sm:text-sm">Payment</TabsTrigger>
                <TabsTrigger value="shipping" className="text-xs sm:text-sm">Shipping</TabsTrigger>
              </>
            )}
            <TabsTrigger value="documents" className="text-xs sm:text-sm">Documents</TabsTrigger>
            <TabsTrigger value="activity" className="text-xs sm:text-sm">Activity Log</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Draft RFQ Guidance Banner */}
            {needsRFQSetup && (
              <Alert className="border-blue-500 bg-blue-50">
                <AlertCircle className="h-5 w-5 text-blue-600" />
                <AlertTitle className="text-blue-900 font-semibold">Next Step: Open RFQ</AlertTitle>
                <AlertDescription className="text-blue-800">
                  <p>
                    This job is in draft status. To receive bids from vendors, you need to open a Request for Quote (RFQ) by clicking the RFQ tab above.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {/* Dispute Status Banner */}
            {job.status === 'disputed' && (
              <DisputeStatusBanner />
            )}

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap">{job.description}</p>
                </div>
              </CardContent>
            </Card>


            {/* Unified Job Timeline */}
            {(() => {
              // Check if user can create milestones based on role and job status
              const canCreateMilestones = (() => {
                if (isVendor) {
                  return job?.status === 'awarded';
                }
                if (isClient) {
                  return true;
                }
                return false;
              })();

              // Milestones can only be added when not fully approved
              const canAddMilestones = canCreateMilestones && approval?.approval_status !== 'approved_by_both';

              return (
                <UnifiedJobTimeline
                  jobId={job.id}
                  jobPublicId={job.public_id}
                  currentUserRole={user?.role === 'client' ? 'client' : 'vendor'}
                  isFullyApproved={approval?.approval_status === 'approved_by_both'}
                  canEdit={user?.role === 'client'}
                  canRequestChange={true}
                  onEditMilestone={handleEditMilestone}
                  onMarkComplete={handleMarkComplete}
                  onRequestChange={handleRequestChange}
                  onAddMilestone={canAddMilestones ? () => navigate(`/jobs/${job.public_id}/milestones?action=add`) : undefined}
                  onApplyTemplate={canAddMilestones ? () => navigate(`/jobs/${job.public_id}/milestones?action=template`) : undefined}
                  onViewMilestones={() => navigate(`/jobs/${job.public_id}/milestones`)}
                  needsMilestoneApproval={needsMilestoneApproval}
                  jobStatus={job.status}
                />
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="rfq">
          <RFQTab job={job} />
        </TabsContent>


        {(job.status === 'awarded' || job.status === 'in_progress' || job.status === 'completed_pending_review' || job.status === 'accepted' || job.status === 'closed') && (
          <>
            <TabsContent value="messages">
              <MessagesTab 
                jobId={job.id}
                jobStatus={job.status}
                organizationId={job.organization_id}
              />
            </TabsContent>

            <TabsContent value="payment">
              <div className="space-y-6">
                {/* ACH Pending Banner in Payment Tab */}
                {job.payment_status === 'ach_pending' && paymentTransaction?.ach_settlement_expected_at && (
                  <AchPendingBanner 
                    settlementDate={paymentTransaction.ach_settlement_expected_at}
                    showDetailedMessage={false}
                  />
                )}
                
                {/* Invoice Pending Card in Payment Tab */}
                {pendingInvoice && (
                  <InvoicePendingCard
                    invoiceNumber={pendingInvoice.stripe_invoice_number || undefined}
                    invoiceUrl={pendingInvoice.hosted_invoice_url || undefined}
                    pdfUrl={pendingInvoice.pdf_url || undefined}
                    amountCents={pendingInvoice.amount_due_cents}
                    status={pendingInvoice.status as 'open' | 'paid' | 'void' | 'uncollectible' | 'payment_failed' | 'draft'}
                    dueDate={pendingInvoice.due_date || undefined}
                    createdAt={pendingInvoice.created_at}
                    paymentContext={pendingInvoice.payment_context as 'initial_payment' | 'change_order' | undefined}
                  />
                )}
                
                {/* Financial Summary */}
                {poFinancialData && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        Payment Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className={`grid grid-cols-2 ${showVendorFinancials ? 'md:grid-cols-5' : 'md:grid-cols-3'} gap-4`}>
                        <div>
                          <p className="text-sm text-muted-foreground">Original PO Amount</p>
                          <p className="text-lg font-semibold">{formatCurrency(poFinancialData.originalAmount)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Adjustments</p>
                          <p className={`text-lg font-semibold ${poFinancialData.totalAdjustments < 0 ? 'text-green-600' : poFinancialData.totalAdjustments > 0 ? 'text-orange-600' : ''}`}>
                            {formatCurrency(poFinancialData.totalAdjustments)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Current PO Total</p>
                          <p className="text-xl font-bold text-primary">{formatCurrency(poFinancialData.currentAmount)}</p>
                        </div>
                        
                        {/* Vendor-only fields */}
                        {showVendorFinancials && vendorFinancialTotals && (
                          <>
                            <div>
                              <p className="text-sm text-muted-foreground">Total Platform Fees</p>
                              <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                                {formatCurrency(vendorFinancialTotals.totalPlatformFees)}
                              </p>
                            </div>
                            <div>
...
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <PaymentStatusCard 
                  paymentStatus={job.payment_status}
                  transaction={paymentTransaction}
                  showVendorFinancials={showVendorFinancials}
                />
                
                {/* Adjustment Payments List */}
                {adjustmentTransactions && adjustmentTransactions.length > 0 && purchaseOrderId && (
                  <AdjustmentPaymentsList 
                    purchaseOrderId={purchaseOrderId}
                    transactions={adjustmentTransactions}
                    showVendorFinancials={showVendorFinancials}
                  />
                )}
                
                {/* Link to Purchase Order for full financial details */}
                {hasPurchaseOrder && purchaseOrderId && (
                  <Card>
                    <CardContent className="pt-6">
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => navigate(`/purchase-orders/${purchaseOrderId}`)}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        View Complete Financial Details on Purchase Order
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="shipping">
              <ShippingTab jobId={job.id} jobStatus={job.status} />
            </TabsContent>
          </>
        )}

        <TabsContent value="documents">
          <JobDocumentsTab jobId={job.id} defaultSubTab={searchParams.get('subtab') as 'files' | 'upload' | undefined} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityLog jobId={job.id} />
        </TabsContent>
      </Tabs>

      <CompletionValidationModal
        open={showValidationModal}
        onOpenChange={setShowValidationModal}
        requirements={requirements}
        onSubmit={handleCompletionSubmit}
        loading={submitting}
      />

      <ClientReviewModal
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        onReview={reviewCompletion}
        loading={submitting}
        submittedAt={completionData?.completion_submitted_at || ''}
        deliveryDate={completion?.delivery_date}
        dueDate={job?.due_date || ''}
        deadlineType={job?.delivery_details?.deadline_type}
        timeRemaining={getTimeRemainingForReview()}
        onJobAccepted={handleJobAccepted}
        purchaseOrderId={purchaseOrderId}
        completionNotes={completion?.completion_notes}
      />

      <AdminReopenModal
        open={showAdminReopenModal}
        onOpenChange={setShowAdminReopenModal}
        onReopen={adminReopenJob}
        loading={submitting}
      />

      {job && (
        <PostJobReviewFlow
          jobId={job.id}
          jobStatus={job.status}
          organizationId={job.organization_id}
          vendorUserId={vendorUserId}
          clientUserId={clientUserId}
          onComplete={() => setShowPostJobReview(false)}
        />
      )}

      {/* Submit Review Modal */}
      {showSubmitReviewModal && job && revieweeId && (
        <ReviewSubmissionModal
          open={showSubmitReviewModal}
          onOpenChange={setShowSubmitReviewModal}
          jobId={job.id}
          revieweeUserId={revieweeId}
          reviewerType={isVendor ? 'vendor' : 'client'}
          organizationId={job.organization_id}
          onSuccess={() => {
            setShowSubmitReviewModal(false);
            setHasPendingReview(false);
          }}
        />
      )}

      {/* Purchase Agreement Modal */}
      <PurchaseAgreementModal
        open={showPurchaseAgreementModal}
        onClose={() => setShowPurchaseAgreementModal(false)}
        onAccept={handleAcceptAgreement}
        onReviewDetails={handleReviewDetails}
        amount={paymentAmount}
        loading={false}
        taxPreview={taxPreview}
      />

      {/* Payment Method Selection Modal - Only for clients */}
      {isClient && (
        <PaymentMethodSelectionModal
          open={showPaymentMethodModal}
          organizationId={job?.organization_id || ''}
          amount={paymentAmount}
          taxAmountCents={taxPreview?.totalTaxCents || 0}
          currentPaymentMethodId={currentPaymentMethodId}
          jobPublicId={job?.public_id}
          onConfirm={handlePaymentMethodConfirmed}
          onCancel={() => setShowPaymentMethodModal(false)}
          loading={creatingPO}
        />
      )}

      {/* Payment Processing Modal */}
      <PaymentProcessingModal
        open={showPaymentProcessingModal}
        amount={paymentAmount}
        status={paymentProcessingStatus}
        errorMessage={paymentError}
        errorDetails={paymentErrorDetails}
        transactionId={paymentTransactionId}
        onClose={() => setShowPaymentProcessingModal(false)}
        onUpdatePaymentMethod={() => {
          setShowPaymentProcessingModal(false);
          setShowPaymentMethodModal(true);
        }}
      />
      
      {/* Milestone Edit Dialog */}
      {showEditMilestoneDialog && editingMilestone && (
        <Dialog open={showEditMilestoneDialog} onOpenChange={setShowEditMilestoneDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Milestone</DialogTitle>
            </DialogHeader>
            <MilestoneForm
              onSubmit={handleUpdateMilestone}
              initialData={editingMilestone}
              mode="edit"
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Milestone Change Request Dialog */}
      {showChangeRequestDialog && requestingChangeMilestone && (
        <Dialog open={showChangeRequestDialog} onOpenChange={(open) => {
          setShowChangeRequestDialog(open);
          if (!open) {
            setChangeReason('');
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Request Milestone Change</DialogTitle>
            </DialogHeader>
            <MilestoneForm
              onSubmit={handleSubmitChangeRequest}
              initialData={requestingChangeMilestone}
              mode="requestChange"
              changeReason={changeReason}
              onChangeReasonUpdate={setChangeReason}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Completion Rejection Modal for Vendors */}
      {job && (
        <CompletionRejectionModal
          open={showRejectionModal}
          onOpenChange={setShowRejectionModal}
          jobTitle={job.title}
          reasonCode={completion?.review_reason_code}
          reviewNotes={completion?.review_notes}
          onAcknowledge={handleAcknowledgeRejection}
        />
      )}

      {/* File Dispute Modal */}
      {job && (
        <FileDisputeModal
          open={showDisputeModal}
          onOpenChange={setShowDisputeModal}
          jobId={job.id}
          jobTitle={job.title}
          onDisputeFiled={() => {
            refetchJob();
            toast({
              title: "Dispute Filed",
              description: "An administrator will review your case and contact both parties.",
            });
          }}
        />
      )}
    </div>
  );
}